import { makeContentLayout } from "../components/ContentWrapper/utils/contentLayoutUtil";

const emptyContentLayout = makeContentLayout();

/**
 * Constant values to set HTML parent element for the grid.
 * Accessibility is the default setting, which makes it an unordered list for grids between
 * 3-9 content items, this is based on the recommendation of the accessibility team.
 */
export const htmlElementsValues = Object.freeze({
    ul: "ul-li",
    div: "div-div",
    accessibility: "accessibility",
});

export const defaultGridOptions = {
    gridClasses: "",
    defaultContent: emptyContentLayout,
    positionalContent: Object.freeze([]),
    htmlElements: htmlElementsValues.accessibility,
};

/**
 * A factory function to build gridLayout objects.
 *
 * @param {Object} gridLayoutOptions this object may contain some or none of the following options:
 * gridClasses - CSS classes applied to grid parent
 * defaultContent - contentLayout applied to all content items unless otherwise overriden
 * positionalContent - An array (possibly with null valued gaps) of contentLayouts which define layouts for content items based on their position in the grid. See readme for examples.
 * htmlElements - Sets the HTML tag for the grid parent and direct children (see htmlElementsValues above).
 * Can also take an existing grid layout object, in which case it makes a deep copy.
 * @returns {Object} The gridLayout object.
 */

export const makeGridLayout = function (gridLayoutOptions = {}) {
    const {
        gridClasses,
        defaultContent,
        positionalContent,
        htmlElements,
    } = gridLayoutOptions;

    const defined = Object.fromEntries(
        Object.entries({
            gridClasses,
            defaultContent,
            htmlElements,
            positionalContent,
        }).filter(([, value]) => value !== undefined)
    );

    const layout = { ...defaultGridOptions, ...defined };

    layout.defaultContent = makeContentLayout(layout.defaultContent);

    layout.positionalContent = Object.freeze(
        layout.positionalContent.map((x) => {
            return x === undefined ? undefined : makeContentLayout(x);
        })
    );

    return Object.freeze({ ...layout, ...gridLayoutMethods });
};

export const gridLayoutMethods = {
    /**
     * Checks length of items and value of htmlElements property to figure out if the grid should be a <div> or a <ul>.
     * @param {number} contentLength the number of grid data items
     * @return {Boolean} true for UL-LI and false for DIV-DIV
     */
    isHtmlList(contentLength) {
        if (this.htmlElements === htmlElementsValues.accessibility) {
            return contentLength >= 3 && contentLength <= 9;
        } else if (this.htmlElements === htmlElementsValues.ul) {
            return true;
        }
        return false;
    },

    /**
     * Specifies the ContentComponent for a gridLayout. The first argument applied to defaultContent. The second argument applied to positionalContent.
     * If either argument is null or undefined, it will leave the existing ContentComponent(s) for that argument unchanged.
     * Does not mutate the gridLayout or contentLayouts.
     * See readme for examples of usage
     *
     * @param {Object} defaultComponent A React component
     * @param {[Object]} positionalComponents Array of React components (with optional null gaps)
     * @return {Object} A new grid layout object with ContentComponent values set
     */
    selectComponents(defaultComponent, positionalComponents = []) {
        // The default component for the grid is selected based on the first argument
        if (defaultComponent === null || defaultComponent === undefined) {
            defaultComponent = this.defaultContent.ContentComponent;
        }

        const defaultContent = this.defaultContent.with({
            ContentComponent: defaultComponent,
        });

        const positionalContent = this.positionalContent.map((layout, i) => {
            if (layout === null || undefined) {
                //This allows blank slots to be left in the container layout, for example if you wanted a special component at the end of a list.
                return null;
            }

            const component = positionalComponents[i];

            return layout.with({ ContentComponent: component });
        });

        return makeGridLayout({
            ...this,
            defaultContent,
            positionalContent,
        });
    },

    /**
     * Takes gridLayout options and returns a new gridLayout with the specified options set.
     * Any unspecified properties will be what they were in the existing gridLayout.
     * This does not mutate the layout. See readme for usage examples.
     * @param {Object} options Content Component options
     * @return {Function} returns MakeGridLayout with ContentComponent options applied
     */
    with(options) {
        return makeGridLayout({ ...this, ...options });
    },
};
